import c from 'classnames';
import { getPictureUrl } from 'core/api/pictures';
import Breadcrumbs from 'core/components/common/Breadcrumbs';
import CenterLoader from 'core/components/common/CenterLoader';
import NotFound from 'core/components/common/NotFound';
import { useLinks } from 'core/components/linksContext';
import { column, container, ieFix, row } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { getRichContent, isFilledArray, isHtmlEmpty } from 'core/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import SocialShare from 'site/components/social-share/SocialShare';
import ViewIcon from '../../svg/eye.svg';
import LecturerIcon from '../../svg/lecturer.svg';
import useArticleData from './hooks/useArticleData';
import { StyledBigArticleCard } from './RenderArticles';

const ArticleDetail: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const [article, loading] = useArticleData();
    const links = useLinks();

    if (loading) {
        return <CenterLoader />;
    }

    // @see \src\site\components\product\public\productDetailFetchData.tsx
    if (!loading && !article) {
        return <NotFound />;
    }

    const { account, suggestedProduct, suggestedArticles, content } = article;
    const richContent = getRichContent(content);

    return (
        <div className={classes.container}>
            <div className={classes.breadcrumbs}>
                <Breadcrumbs
                    contrast={false}
                    items={[
                        { href: links.home && links.home.url(), title: 'Главная' },
                        { href: links.articles.url(), title: 'Статьи' },
                        { title: article.title },
                    ]}
                />
            </div>

            <div className={classes.title}>{article.title}</div>

            <div className={classes.tagsPanel}>
                <div className={classes.pageViews}>
                    <ViewIcon className={classes.viewsLogo} />
                    <p className={classes.subtitle}>{article.pageViews}</p>
                </div>
                <div className={classes.author}>
                    <LecturerIcon className={classes.logo} />
                    <links.teacherDetail.Link
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.name}
                        to={links.teacherDetail.url(account.id)}
                    >
                        {account.firstName} {account.lastName}
                    </links.teacherDetail.Link>
                </div>
                {isFilledArray(article.tags) && <p className={classes.subtitle}>Темы:</p>}
                {isFilledArray(article.tags) &&
                    article.tags.map((tag, index) => (
                        <div className={classes.tag} key={index}>
                            {tag}
                        </div>
                    ))}
            </div>

            {!isHtmlEmpty(richContent) && (
                <div className={classes.text} dangerouslySetInnerHTML={{ __html: richContent }} />
            )}

            <SocialShare />

            {suggestedProduct && (
                <>
                    <p className={c(classes.title, classes.textTitle)}>Рекомендуемый курс</p>
                    <div className={classes.suggestedCards}>
                        <div
                            className={classes.product}
                            style={{
                                background: `url("${getPictureUrl(
                                    suggestedProduct.pictureId,
                                    '570x305',
                                    null,
                                )}") center center / cover no-repeat`,
                            }}
                        >
                            <Link className={classes.background} to={links.productDetail.url(suggestedProduct.id)} />
                            <Link className={classes.productLink} to={links.productDetail.url(suggestedProduct.id)}>
                                <p className={classes.productName}>{suggestedProduct.name}</p>
                            </Link>
                        </div>
                    </div>
                </>
            )}

            {isFilledArray(suggestedArticles) && (
                <>
                    <p className={c(classes.title, classes.textTitle)}>Рекомендуем почитать</p>
                    <div className={classes.suggestedCards}>
                        {suggestedArticles.map((article, index) => (
                            <StyledBigArticleCard article={article} key={index} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            ...container(theme),
        },
        breadcrumbs: {
            ...column(),
            paddingLeft: 0,
        },
        title: {
            fontSize: 1.75 + 'rem',
            fontWeight: 700,
            marginBottom: 1.25 + 'rem',
            color: theme.colors.contrastBg,

            [theme.breakpoints.down('xs')]: {
                fontSize: 1 + 'rem',
                marginBottom: 1 + 'rem',
                marginLeft: 0,
                marginRight: 0,
            },
        },
        textTitle: {
            fonSize: 1.25 + 'rem',
            textAlign: 'center',
        },
        tagsPanel: {
            ...row(),

            '& $tag': {
                marginLeft: 0,
            },

            '& $tag ~ $tag': {
                marginLeft: 15,
            },
        },
        tag: {
            paddingLeft: 9,
            paddingRight: 9,
            marginRight: 0,
            marginBottom: 15,
            fontWeight: 600,
            fontSize: 0.75 + 'rem',
            fontFamily: 'Arial, sans-serif',
            textTransform: 'uppercase',
            borderColor: '#cccccc',
            borderRadius: 0,
            borderStyle: 'solid',
            borderWidth: 1,

            [theme.breakpoints.down('xs')]: {
                marginBottom: 10,
            },
        },
        pageViews: {
            ...row(),
            marginLeft: 0,
            marginRight: 0,
        },
        viewsLogo: {
            display: 'inline-block',
            width: 1 + 'rem',
            height: 1 + 'rem',
            fill: theme.colors.accentLight,
            marginTop: 5,
            marginLeft: 15,
        },
        subtitle: {
            paddingLeft: 9,
            paddingRight: 9,
            marginBottom: 15,
            fontWeight: 700,
            fontSize: 0.85 + 'rem',
            fontFamily: 'Arial, sans-serif',

            [theme.breakpoints.down('xs')]: {
                marginBottom: 10,
            },
        },
        author: {
            ...row(),
            paddingLeft: 6,
            paddingRight: 9,
            marginLeft: 15,
            marginRight: 15,
            marginBottom: 15,
            borderColor: '#cccccc',
            borderRadius: 0,
            borderStyle: 'solid',
            borderWidth: 1,

            [theme.breakpoints.down('xs')]: {
                marginBottom: 10,
            },
        },
        logo: {
            display: 'inline-block',
            width: 0.75 + 'rem',
            height: 0.75 + 'rem',
            fill: theme.colors.accentLight,
            margin: 'auto 5px auto 0',
        },
        name: {
            marginBottom: 0,
            fontSize: 0.85 + 'rem',
            fontFamily: 'Arial, sans-serif',
            textDecoration: 'unset',
            color: theme.colors.contrastBg,

            ...ieFix({ textDecoration: 'none' }),
        },
        text: {
            fontSize: 1 + 'rem',
            lineHeight: '27px',
            wordWrap: 'break-word',

            '& img': {
                maxWidth: '100%',
                maxHeight: 'fit-content',
                height: 'auto !important',
                objectFit: 'contain',
            },
            '& iframe': {
                maxWidth: '100%',
                [theme.breakpoints.down('sm')]: {
                    height: '50vw',
                },
                [theme.breakpoints.down('xs')]: {
                    height: 179,
                },
            }
        },
        product: {
            minWidth: 40 + '%',
            maxWidth: 47.5 + '%',
            flexShrink: 0,
            flexGrow: 1,
            flexDirection: 'column',
            justifyContent: 'flex-end',
            boxSizing: 'border-box',
            paddingLeft: 15,
            paddingRight: 15,
            height: 305,
            margin: 'auto auto 40px auto',
            fontFamily: 'Arial, sans-serif',
            display: 'flex',
            position: 'relative',

            [theme.breakpoints.down('xs')]: {
                maxWidth: 100 + '%',
                height: 160,
                marginLeft: 0,
                marginRight: 0,
            },
        },
        background: {
            backgroundColor: '#191717',
            opacity: 0.3,
            position: 'absolute',
            left: 0,
            top: 0,
            height: 100 + '%',
            width: 100 + '%',
            zIndex: 0,
            transition: 'background 0.5s ease-out, color 0.5s ease-out, box-shadow 0.5s ease-out',

            '&:hover, &:focus': {
                backgroundColor: 'rgba(25, 23, 23, 0.60)',
            },
        },
        productLink: {
            textDecoration: 'unset',
            zIndex: 1,

            ...ieFix({ textDecoration: 'none' }),
        },
        productName: {
            zIndex: 1,
            fontSize: 1.3 + 'rem',
            fontWeight: 700,
            marginBottom: 0.6 + 'rem',
            color: theme.colors.lightBg,

            [theme.breakpoints.down('xs')]: {
                fontSize: 1 + 'rem',
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
            },
        },
        suggestedCards: {
            ...row(),
        },
    });

export default injectSheet(styles, 'ArticleDetail')(ArticleDetail);
